import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    ArrowLeft, Ban, Camera, CheckCircle2, ChevronLeft, ChevronRight, Copy, FileText, Loader2,
    Paperclip, Pencil, Send, Trash2, Truck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Combobox } from '../../../components/ui/combobox';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
    Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink,
} from '../../../components/ui/pagination';
import { Textarea } from '../../../components/ui/textarea';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import AccessRestricted from '../../../components/AccessRestricted';
import { ImageLightbox } from '../../../components/ui/image-lightbox';
import type { LightboxImage } from '../../../components/ui/image-lightbox';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { Project, Vendor } from '../../../types/lists.types';
import { Product } from '../../../types/stock.types';
import {
    PurchaseOrderDetail,
    PurchaseOrderDocument,
    PurchaseOrderPhoto,
    PurchaseOrderReceiptStatus,
    PurchaseOrderStatus,
    ReceiptLineCondition,
} from '../../../types/procurement.types';
import { ApiError } from '../../../utils/api';
import { projectsApi, vendorsApi } from '../../../utils/lists-api';
import { purchaseOrdersApi } from '../../../utils/procurement-api';
import { productsApi } from '../../../utils/stock-api';
import { compressImage } from '../../../utils/upload';
import { usePageTitle } from '../../../utils/usePageTitle';
import { daysAgo, formatDate, formatDateTime, money, orderReference, vendorName } from './procurement-ui';
import AmendmentPanel from './components/AmendmentPanel';
import PurchaseOrderLineEditor, {
    lineDraftFromLine,
    lineDraftPayloads,
    orderDraftIssue,
    PurchaseOrderLineDraft,
} from './components/PurchaseOrderLineEditor';
import {
    AmendmentChip, ClosedShortChip, LineStatusPill, PurchaseOrderStatusPill,
    purchaseOrderStatusLabel, receivedPercentLabel,
} from './components/PurchaseOrderStatus';

const RECEIPT_STATUS_LABELS: Record<PurchaseOrderReceiptStatus, string> = {
    in_progress: 'In progress',
    posted: 'Posted',
    voided: 'Voided',
};

const RECEIPT_STATUS_STYLES: Record<PurchaseOrderReceiptStatus, string> = {
    in_progress: 'text-blue-700 bg-blue-50',
    posted: 'text-emerald-800 bg-emerald-100',
    voided: 'text-red-700 bg-red-50',
};

const CONDITION_LABELS: Record<ReceiptLineCondition, string> = {
    good: 'Good',
    damaged: 'Damaged',
    short: 'Short',
};

const CONDITION_STYLES: Record<ReceiptLineCondition, string> = {
    good: 'text-emerald-700 bg-emerald-50',
    damaged: 'text-amber-700 bg-amber-50',
    short: 'text-red-700 bg-red-50',
};

const TONE_STYLES = {
    red: 'border-red-200 bg-red-50/60 text-red-800',
    amber: 'border-amber-200 bg-amber-50/60 text-amber-900',
    emerald: 'border-emerald-200 bg-emerald-50/60 text-emerald-800',
} as const;

/**
 * Mirrors EDITABLE_STATUSES in `purchaseOrder.routes.js`. `canUpdate` says the caller may
 * edit purchase orders; this says which ones the API still accepts a write against, so the
 * Edit button is never offered on a record that would only 409.
 */
const EDITABLE_STATUSES: PurchaseOrderStatus[] = [
    'draft', 'submitted', 'approved', 'po_issued', 'receiving',
];

/** The reason dialogs on this page, each with the minimum the API enforces. */
type ReasonKind = 'reject' | 'cancel' | 'close' | 'void';

const REASON_COPY: Record<ReasonKind, {
    title: (reference: string) => string;
    body: string;
    placeholder: string;
    confirm: string;
    min: number;
}> = {
    reject: {
        title: (reference) => `Reject ${reference}`,
        body: 'The requester keeps a read-only copy and can duplicate it into a fresh draft. ' +
            'Say what needs to change.',
        placeholder: 'e.g. Price is above the signed rate card — re-quote.',
        confirm: 'Reject request',
        min: 1,
    },
    close: {
        title: (reference) => `Close ${reference} short`,
        body: 'The unreceived quantity on every open line is written off and the PO moves straight ' +
            'to Ready to Enter. The closed-short flag and this reason stay on the record permanently.',
        placeholder: 'e.g. Anchor bolts discontinued by vendor, confirmed by phone 14 Sep — ' +
            'alternative to be sourced separately.',
        confirm: 'Close short',
        min: 20,
    },
    cancel: {
        title: (reference) => `Cancel ${reference}`,
        body: 'Terminal. The PO is called off with goods still outstanding and nothing further can ' +
            'be received against it.',
        placeholder: 'e.g. Project scope changed, works deferred to next quarter — vendor notified 5 Mar.',
        confirm: 'Cancel PO',
        min: 20,
    },
    void: {
        title: (reference) => `Void receipt ${reference}`,
        body: 'The quantities this receipt applied are rolled back and the PO returns to where it ' +
            'was. The void reason stays on the record.',
        placeholder: 'e.g. Docket entered against the wrong PO — corrected and re-keyed 14 Sep.',
        confirm: 'Void receipt',
        min: 20,
    },
};

const serverMessage = (err: unknown, fallback: string) => {
    if (err instanceof ApiError) {
        // Error bodies are `{ message }` — see the backend route conventions.
        const body = err.data as { message?: string } | null;
        return body?.message || err.message || fallback;
    }
    return err instanceof Error ? err.message || fallback : fallback;
};

/** History entries are one line each; ten keeps the card readable without hiding the arc. */
const HISTORY_PAGE_SIZE = 10;

/**
 * Page numbers to draw for a page count, with a gap either side of the reader: first, last
 * and ±1 around the current page — the same window the accounts table uses.
 */
const pageWindow = (totalPages: number, current: number): (number | 'gap-start' | 'gap-end')[] => {
    const pages: (number | 'gap-start' | 'gap-end')[] = [1];
    const from = Math.max(2, current - 1);
    const to = Math.min(totalPages - 1, current + 1);
    if (from > 2) pages.push('gap-start');
    for (let page = from; page <= to; page += 1) pages.push(page);
    if (to < totalPages - 1) pages.push('gap-end');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
};

/**
 * Purchase Order record.
 *
 * Everything in the action bar comes from `order.abilities`, never from a status guess: the
 * server decides who may submit, approve, issue, close short, cancel, enter or receive, and
 * the UI renders exactly that set. A status is still needed for the transitions the server
 * restricts to one leg of the lifecycle (a draft can only be submitted, for instance), but it
 * never grants an action on its own.
 *
 * Receiving is deliberately not completed here: the capture flow lives at
 * /stock/procurement/receiving/:id on a phone. This page shows the evidence it produced and
 * lets AP void a posted receipt, which is the only correction path the data model has.
 */
export default function PurchaseOrderDetailPage() {
    usePageTitle();
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER);
    /**
     * Duplicating a rejected/cancelled record is the one action with no `abilities` flag —
     * it is gated by create:purchase_order, so it reads the CASL ability the server derives
     * its own answer from.
     */
    const canDuplicate = ability.can(ACTIONS.CREATE, RESOURCES.PURCHASE_ORDER);
    const { id } = useParams<{ id: string }>();

    const [order, setOrder] = useState<PurchaseOrderDetail | null>(null);
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [products, setProducts] = useState<Product[]>([]);

    const [reasonDialog, setReasonDialog] = useState<{ kind: ReasonKind; receiptId?: number; reference?: string } | null>(null);
    const [reason, setReason] = useState('');

    const [enterOpen, setEnterOpen] = useState(false);
    const [externalRef, setExternalRef] = useState('');
    const [enteredInventory, setEnteredInventory] = useState(false);
    const [enteredApprovalMax, setEnteredApprovalMax] = useState(false);

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editVendorId, setEditVendorId] = useState('');
    const [editProjectId, setEditProjectId] = useState('');
    const [editRequiredBy, setEditRequiredBy] = useState('');
    const [editNotes, setEditNotes] = useState('');
    const [editLines, setEditLines] = useState<PurchaseOrderLineDraft[]>([]);

    const [removing, setRemoving] = useState<
        { kind: 'photo' | 'document' | 'order-document'; receiptId?: number; id: number; label: string } | null
    >(null);
    const [renamingDocument, setRenamingDocument] = useState<PurchaseOrderDocument | null>(null);
    const [renameFilename, setRenameFilename] = useState('');
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [historyPage, setHistoryPage] = useState(1);
    const photoInputs = useRef<Record<number, HTMLInputElement | null>>({});
    const documentInputs = useRef<Record<number, HTMLInputElement | null>>({});
    const orderDocumentInput = useRef<HTMLInputElement | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setOrder(await purchaseOrdersApi.get(id));
            setAccessDenied(null);
        } catch (err) {
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            if (err instanceof ApiError && err.status === 404) {
                setNotFound(true);
                return;
            }
            toast.error(serverMessage(err, 'Failed to load the purchase order'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    // A different PO is a different history — the reader starts at its newest entry.
    useEffect(() => { setHistoryPage(1); }, [id]);

    useEffect(() => {
        if (!canRead) return;
        vendorsApi.list({ status: 'active' }).then(setVendors).catch(() => undefined);
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
    }, [canRead]);

    // The edit dialog's project list belongs to the order's branch.
    const branchId = order?.branch.id;
    useEffect(() => {
        if (!canRead || !branchId) return;
        projectsApi.list({ status: 'active', branchId: String(branchId) })
            .then(setProjects).catch(() => undefined);
    }, [canRead, branchId]);

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return (
            <AccessRestricted
                accessMessage={accessDenied}
                backMessage="Back to Purchase Orders"
                navigateTo="/stock/procurement/purchase-orders"
            />
        );
    }

    if (notFound) {
        return (
            <div className="flex flex-col items-center justify-center h-[50vh] space-y-4">
                <FileText size={48} className="text-muted-foreground" />
                <h2 className="text-xl font-semibold">Purchase order not found</h2>
                <p className="text-muted-foreground">
                    It may have been deleted while still a draft.
                </p>
                <Button variant="outline" onClick={() => navigate('/stock/procurement/purchase-orders')}>
                    Back to Purchase Orders
                </Button>
            </div>
        );
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const reference = orderReference(order);
    const { abilities } = order;
    const outstandingUnits = Math.max(0, order.orderedUnits - order.receivedUnits);
    const waitingDays = daysAgo(order.submittedAt);
    const orderDocuments = order.documents ?? [];
    /**
     * Receipts that are still being counted. Posting is what applies quantities, so an
     * unposted receipt holds a count the order has not booked: closing short now would land
     * the PO in Ready to Enter with a live receipt still against it, which is exactly the
     * state the API refuses to create (see the close-short route). The check mirrors that
     * rule rather than second-guessing it — a receipt that slips in behind this read is still
     * stopped by the 409.
     */
    const openReceipts = order.receipts.filter((receipt) => receipt.status === 'in_progress');

    const run = async (key: string, action: () => Promise<void>) => {
        try {
            setBusy(key);
            await action();
            await load();
        } catch (err) {
            toast.error(serverMessage(err, 'Something went wrong'));
        } finally {
            setBusy(null);
        }
    };

    const submit = () => run('submit', async () => {
        const updated = await purchaseOrdersApi.submit(order.id);
        toast.success(updated.status === 'pending_approval'
            ? 'Submitted — routed for approval'
            : 'Submitted to AP');
    });

    const duplicate = async () => {
        try {
            setBusy('duplicate');
            const copy = await purchaseOrdersApi.duplicate(order.id);
            toast.success('Copied into a new draft — edit and resubmit');
            setBusy(null);
            navigate(`/stock/procurement/purchase-orders/${copy.id}`);
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to duplicate the purchase order'));
            setBusy(null);
        }
    };

    const approve = () => run('approve', async () => {
        await purchaseOrdersApi.approve(order.id);
        toast.success('Approved — now with AP to issue');
    });

    const issue = () => run('issue', async () => {
        const issued = await purchaseOrdersApi.issue(order.id);
        toast.success(`PO issued as ${issued.poNumber ?? reference}`);
    });

    const confirmReason = () => {
        if (!reasonDialog) return;
        const trimmed = reason.trim();
        const copy = REASON_COPY[reasonDialog.kind];
        if (trimmed.length < copy.min) return;

        const kind = reasonDialog.kind;
        const receiptId = reasonDialog.receiptId;
        if (kind === 'void' && receiptId === undefined) return;
        run(`reason:${kind}`, async () => {
            if (kind === 'reject') {
                await purchaseOrdersApi.reject(order.id, trimmed);
                toast.success('Rejected — the requester can duplicate it as a new draft');
            } else if (kind === 'cancel') {
                await purchaseOrdersApi.cancel(order.id, trimmed);
                toast.success('PO cancelled');
            } else if (kind === 'close') {
                await purchaseOrdersApi.closeShort(order.id, trimmed);
                toast.success('Closed short — now in the Ready to Enter queue');
            } else if (receiptId !== undefined) {
                await purchaseOrdersApi.voidReceipt(order.id, receiptId, trimmed);
                toast.success('Receipt voided — the quantities have been rolled back');
            }
            setReasonDialog(null);
            setReason('');
        });
    };

    /**
     * Close Short is refused while a receipt is mid-count. The receiver may be standing in the
     * yard with the docket half keyed, and the write-off would retire the order underneath
     * them; so the block is explained here, where the click happened, rather than left to the
     * API's 409 after a reason has been typed.
     */
    const startCloseShort = () => {
        if (openReceipts.length > 0) {
            const dockets = openReceipts.map((receipt) => receipt.docketNumber).join(', ');
            toast.error(
                `Receipt ${dockets} is still in progress — it has to be posted before ` +
                `${reference} can be closed short.`,
            );
            return;
        }
        setReason('');
        setReasonDialog({ kind: 'close' });
    };

    const confirmEnter = () => {
        if (!externalRef.trim() || !enteredInventory || !enteredApprovalMax) return;
        run('enter', async () => {
            await purchaseOrdersApi.enter(order.id, { externalRef: externalRef.trim() });
            toast.success('Entered — record locked');
            setEnterOpen(false);
            setExternalRef('');
            setEnteredInventory(false);
            setEnteredApprovalMax(false);
        });
    };

    const confirmDelete = async () => {
        try {
            setBusy('delete');
            await purchaseOrdersApi.remove(order.id);
            toast.success('Draft deleted');
            setDeleteOpen(false);
            setBusy(null);
            navigate('/stock/procurement/purchase-orders');
        } catch (err) {
            toast.error(serverMessage(err, 'Failed to delete the draft'));
            setBusy(null);
        }
    };

    /**
     * Lines a receipt already claims. The API refuses to delete one, so the editor has to
     * know before it offers the button. Voided receipts count — the server's own check asks
     * whether any receipt line references the row, not whether its receipt still stands.
     */
    const receiptedLineIds = new Set(
        order.receipts
            .flatMap((receipt) => receipt.lines.map((line) => line.purchaseOrderLineId))
            .filter((lineId): lineId is number => Number.isFinite(lineId)));

    const openEdit = () => {
        setEditVendorId(order.vendor ? String(order.vendor.id) : '');
        setEditProjectId(order.project ? String(order.project.id) : '');
        setEditRequiredBy(order.requiredByDate ? order.requiredByDate.slice(0, 10) : '');
        setEditNotes(order.notes ?? '');
        setEditLines(order.lines.map((line) => lineDraftFromLine(line, receiptedLineIds.has(line.id))));
        setEditOpen(true);
    };

    const saveEdit = () => {
        const lines = lineDraftPayloads(editLines);
        if (lines.length === 0 || orderDraftIssue(editLines)) return;
        run('edit', async () => {
            await purchaseOrdersApi.update(order.id, {
                vendorId: editVendorId ? Number(editVendorId) : null,
                projectId: editProjectId ? Number(editProjectId) : null,
                requiredByDate: editRequiredBy || null,
                notes: editNotes.trim() || null,
                lines,
            });
            toast.success('Purchase order updated');
            setEditOpen(false);
        });
    };

    const uploadPhoto = (receiptId: number, file: File) => run(`photo:${receiptId}`, async () => {
        // A phone capture is several megabytes; the receipt photo only has to show the gear.
        const compressed = await compressImage(file);
        await purchaseOrdersApi.uploadReceiptPhoto(order.id, receiptId, compressed);
        toast.success('Photo added');
    });

    const uploadDocument = (receiptId: number, file: File) => run(`document:${receiptId}`, async () => {
        await purchaseOrdersApi.uploadReceiptDocument(order.id, receiptId, file);
        toast.success('Document attached');
    });

    const uploadOrderDocument = (file: File) => run('order-document', async () => {
        await purchaseOrdersApi.uploadDocument(order.id, file);
        toast.success('Attachment added');
    });

    const confirmRenameDocument = () => {
        if (!renamingDocument || !renameFilename.trim()) return;
        run('rename-document', async () => {
            // The server keeps the original extension when the name omits one.
            await purchaseOrdersApi.renameDocument(
                order.id, renamingDocument.id, renameFilename.trim());
            toast.success('Attachment renamed');
            setRenamingDocument(null);
            setRenameFilename('');
        });
    };

    const confirmRemove = () => {
        if (!removing) return;
        const { kind, receiptId, id: targetId } = removing;
        run('remove', async () => {
            if (kind === 'photo' && receiptId !== undefined) {
                await purchaseOrdersApi.deleteReceiptPhoto(order.id, receiptId, targetId);
                toast.success('Photo removed');
            } else if (kind === 'document' && receiptId !== undefined) {
                await purchaseOrdersApi.deleteReceiptDocument(order.id, receiptId, targetId);
                toast.success('Document removed');
            } else {
                await purchaseOrdersApi.deleteDocument(order.id, targetId);
                toast.success('Attachment removed');
            }
            setRemoving(null);
        });
    };

    /* --------------------------------------------------------------- actions */

    const status = order.status;
    /**
     * Same pair of conditions the API enforces: `canUpdate` is update:purchase_order, and
     * EDITABLE_STATUSES mirrors the statuses the route still accepts a write against.
     */
    const canEdit = abilities.canUpdate && EDITABLE_STATUSES.includes(status);
    const actions: {
        key: string;
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        destructive?: boolean;
        primary?: boolean;
    }[] = [];

    if (abilities.canSubmit && status === 'draft') {
        actions.push({ key: 'submit', label: 'Submit', icon: <Send size={16} />, onClick: submit, primary: true });
    }
    if (abilities.canDelete && status === 'draft') {
        actions.push({
            key: 'delete', label: 'Delete draft', icon: <Trash2 size={16} />,
            onClick: () => setDeleteOpen(true), destructive: true,
        });
    }
    if (canDuplicate && (status === 'rejected' || status === 'cancelled')) {
        actions.push({ key: 'duplicate', label: 'Duplicate', icon: <Copy size={16} />, onClick: duplicate });
    }
    if (abilities.canApprove && status === 'pending_approval') {
        actions.push({ key: 'approve', label: 'Approve', icon: <CheckCircle2 size={16} />, onClick: approve, primary: true });
    }
    if (abilities.canReject && status === 'pending_approval') {
        actions.push({
            key: 'reject', label: 'Reject', icon: <Ban size={16} />, destructive: true,
            onClick: () => { setReason(''); setReasonDialog({ kind: 'reject' }); },
        });
    }
    if (abilities.canIssue && (status === 'submitted' || status === 'approved')) {
        actions.push({ key: 'issue', label: 'Issue PO', icon: <Send size={16} />, onClick: issue, primary: true });
    }
    if (abilities.canCloseShort && (status === 'po_issued' || status === 'receiving')) {
        actions.push({
            key: 'close', label: 'Close Short', icon: <Ban size={16} />, destructive: true,
            onClick: startCloseShort,
        });
    }
    if (abilities.canCancel
        && ['submitted', 'approved', 'po_issued', 'receiving'].includes(status)) {
        actions.push({
            key: 'cancel', label: 'Cancel', icon: <Ban size={16} />, destructive: true,
            onClick: () => { setReason(''); setReasonDialog({ kind: 'cancel' }); },
        });
    }
    if (abilities.canEnter && status === 'ready_to_enter') {
        actions.push({
            key: 'enter', label: 'Enter', icon: <CheckCircle2 size={16} />, primary: true,
            onClick: () => setEnterOpen(true),
        });
    }
    if (abilities.canReceipt && (status === 'po_issued' || status === 'receiving')) {
        actions.push({
            key: 'receipt', label: 'Log a receipt', icon: <Truck size={16} />,
            onClick: () => navigate(`/stock/procurement/receiving/${order.id}`),
        });
    }

    /* ---------------------------------------------------------------- banner */

    const enteredSystems = [
        order.enteredInventorySoftware ? 'inventory software' : null,
        order.enteredApprovalMax ? 'ApprovalMax' : null,
    ].filter(Boolean).join(' + ');

    // Terminal states win over the closed-short annotation: a PO that was closed short and
    // later entered shows the Entered banner, not a stale "Closed short by ..." — the
    // write-off is still visible as the closed-short chip and in the history.
    const banner: { tone: keyof typeof TONE_STYLES; title: string; body: string } | null =
        status === 'rejected'
            ? {
                tone: 'red',
                title: `Rejected by ${order.rejectedBy?.name ?? 'an approver'} on ${formatDate(order.rejectedAt)}`,
                body: `${order.rejectionReason ?? ''} — this record is read-only history; ` +
                    `duplicate it into a fresh draft to re-raise.`,
            }
            : status === 'entered'
                ? {
                    tone: 'emerald',
                    title: 'Entered — record locked',
                    body: `External reference ${order.externalRef ?? '—'} · ` +
                        `${enteredSystems || 'entered into the accounting systems'}. No further ` +
                        `edits are possible; corrections happen in the accounting systems.`,
                }
                : status === 'cancelled'
                    ? {
                        tone: 'red',
                        title: `Cancelled by ${order.cancelledBy?.name ?? 'AP'} on ${formatDate(order.cancelledAt)}`,
                        body: order.cancellationReason ?? '',
                    }
                    : order.closedShort
                        ? {
                            tone: 'amber',
                            title: `Closed short by ${order.closedShortBy?.name ?? 'AP'} on ${formatDate(order.closedShortAt)}`,
                            body: order.closedShortReason ?? '',
                        }
                        : status === 'pending_approval'
                            ? {
                                tone: 'amber',
                                title: `Waiting on approval — ${waitingDays} ${waitingDays === 1 ? 'day' : 'days'}`,
                                body: `${money(order.totalValue)} is over the ${order.branch.name} threshold of ` +
                                    `${money(order.thresholdAmount)}. The threshold was snapshotted when this ` +
                                    `request was submitted, so a later change to the rule does not rewrite it.`,
                            }
                            : null;

    /* -------------------------------------------------------------- receipts */

    const photoEntries = order.receipts.flatMap((receipt) =>
        receipt.photos.map((photo) => ({ receipt, photo })));
    const photoPositions = new Map<number, number>();
    photoEntries.forEach((entry, index) => photoPositions.set(entry.photo.id, index));
    const lightboxImages: LightboxImage[] = photoEntries.map(({ receipt, photo }) => ({
        id: photo.id,
        url: photo.url,
        label: receipt.docketNumber,
        // `capturedBy` is declared on the contract but the read routes currently return the
        // photo's raw row (`userName`), so the caption must not assume the nested object.
        caption: `${photo.capturedBy?.name ?? 'Not recorded'} · ${formatDateTime(photo.serverTimestamp)}`,
    }));

    const facts: { label: string; value: string }[] = [
        { label: 'Branch', value: order.branch.name },
        { label: 'Vendor', value: vendorName(order.vendor) },
        { label: 'Project', value: order.project?.name ?? 'No project (yard / overhead)' },
        { label: 'Requested by', value: order.requester.name },
        { label: 'Required by', value: formatDate(order.requiredByDate) },
        { label: 'Total (ex GST)', value: money(order.totalValue) },
        {
            label: 'Received',
            value: order.receivedPercent === null
                ? (order.closedShort ? 'Closed short' : '—')
                : receivedPercentLabel(order.receivedPercent),
        },
        {
            label: 'Outstanding',
            value: order.closedShort
                ? `${outstandingUnits} written off`
                : `${outstandingUnits} units`,
        },
    ];

    const history = [...order.history].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    // An action appends entries, so the page count only ever grows — but clamp anyway: the
    // rendered slice must never be empty because a page number outlived its pages.
    const historyPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
    const safeHistoryPage = Math.min(historyPage, historyPages);
    const historyFrom = (safeHistoryPage - 1) * HISTORY_PAGE_SIZE;
    const historyWindow = history.slice(historyFrom, historyFrom + HISTORY_PAGE_SIZE);
    const historyTo = historyFrom + historyWindow.length;
    const reasonCopy = reasonDialog ? REASON_COPY[reasonDialog.kind] : null;
    const reasonValid = reasonCopy !== null && reason.trim().length >= reasonCopy.min;
    const impact = order.lines.filter((line) => line.outstanding > 0);

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-5xl">
            <div>
                <Button variant="ghost" size="sm" className="mb-2 -ml-2"
                        onClick={() => navigate('/stock/procurement/purchase-orders')}>
                    <ArrowLeft size={16} className="mr-1.5" />Purchase Orders
                </Button>
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl font-bold font-mono">{reference}</h1>
                            <PurchaseOrderStatusPill status={status} />
                            {order.closedShort && <ClosedShortChip />}
                            <AmendmentChip count={order.openAmendmentCount} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {vendorName(order.vendor)} · {order.branch.name} · raised{' '}
                            {formatDate(order.createdAt)} by {order.requester.name}
                        </p>
                    </div>

                    {actions.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {actions.map((action) => (
                                <Button
                                    key={action.key}
                                    variant={action.primary ? 'default' : 'outline'}
                                    className={action.destructive ? 'text-destructive' : undefined}
                                    disabled={busy !== null}
                                    onClick={action.onClick}
                                >
                                    {busy === action.key
                                        ? <Loader2 size={16} className="animate-spin mr-2" />
                                        : <span className="mr-2">{action.icon}</span>}
                                    {action.label}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {banner && (
                <div className={`rounded-lg border p-4 text-sm ${TONE_STYLES[banner.tone]}`}>
                    <p className="font-medium">{banner.title}</p>
                    {banner.body && <p className="mt-1 leading-relaxed">{banner.body}</p>}
                    {status === 'rejected' && canDuplicate && (
                        <Button variant="outline" size="sm" className="mt-3 bg-white"
                                disabled={busy !== null} onClick={duplicate}>
                            <Copy size={14} className="mr-1.5" />Duplicate as new draft
                        </Button>
                    )}
                </div>
            )}

            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-start justify-between gap-3 mb-3">
                        <h2 className="font-semibold">Details</h2>
                        {canEdit && (
                            <Button variant="outline" size="sm" onClick={openEdit} disabled={busy !== null}>
                                <Pencil size={14} className="mr-1.5" />Edit
                            </Button>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
                        {facts.map((fact) => (
                            <div key={fact.label}>
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                    {fact.label}
                                </p>
                                <p className="text-sm mt-0.5 break-words">{fact.value}</p>
                            </div>
                        ))}
                    </div>
                    {order.notes && (
                        <div className="mt-4 border-t border-border pt-3">
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Notes
                            </p>
                            <p className="text-sm mt-1 whitespace-pre-wrap">{order.notes}</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="font-semibold">Quotation / attachments</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Order paperwork — the vendor quotation, the signed PO.
                            </p>
                        </div>
                        {abilities.canUploadDocument && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={busy !== null}
                                onClick={() => orderDocumentInput.current?.click()}
                            >
                                {busy === 'order-document'
                                    ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                    : <Paperclip size={14} className="mr-1.5" />}
                                Attach
                            </Button>
                        )}
                    </div>
                    <input
                        ref={orderDocumentInput}
                        type="file"
                        accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx"
                        className="hidden"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            // Reset so picking the same file again still fires onChange.
                            e.target.value = '';
                            if (file) uploadOrderDocument(file);
                        }}
                    />
                    {orderDocuments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No quotation or paperwork attached.
                        </p>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-lg">
                            {orderDocuments.map((document) => (
                                <div key={document.id} className="flex items-center gap-3 p-2">
                                    <span className="w-9 h-9 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                        <FileText size={16} className="text-muted-foreground" />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                        <a href={document.url} target="_blank" rel="noreferrer"
                                           className="block text-sm truncate underline">
                                            {document.filename}
                                        </a>
                                        <span className="block text-xs text-muted-foreground">
                                            {document.uploadedBy?.name ?? 'Unknown'} ·{' '}
                                            {formatDateTime(document.createdAt)}
                                        </span>
                                    </span>
                                    {abilities.canUploadDocument && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Rename ${document.filename}`}
                                            disabled={busy !== null}
                                            onClick={() => {
                                                setRenamingDocument(document);
                                                setRenameFilename(
                                                    document.filename.replace(/\.[^/.]+$/, ''));
                                            }}
                                        >
                                            <Pencil size={14} />
                                        </Button>
                                    )}
                                    {abilities.canDeleteDocument && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Remove ${document.filename}`}
                                            disabled={busy !== null}
                                            onClick={() => setRemoving({
                                                kind: 'order-document',
                                                id: document.id,
                                                label: document.filename,
                                            })}
                                        >
                                            <Trash2 size={14} />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="mb-3">
                        <h2 className="font-semibold">Lines</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            Prices ex GST, AUD. Outstanding is derived — it is what the next
                            receipt can still claim.
                        </p>
                    </div>

                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="font-semibold text-slate-900">Line</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Ordered</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Received</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Outstanding</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Unit price</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Line total</TableHead>
                                    <TableHead className="font-semibold text-slate-900">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {order.lines.map((line) => (
                                    <TableRow key={line.id}>
                                        <TableCell className="min-w-[12rem]">
                                            <span className="block">{line.description}</span>
                                            <span className="block text-xs text-muted-foreground font-mono">
                                                {line.sku ?? 'Custom line'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {line.quantityOrdered} <span className="text-xs text-muted-foreground">{line.uom}</span>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">{line.quantityReceived}</TableCell>
                                        <TableCell className="text-right tabular-nums">{line.outstanding}</TableCell>
                                        <TableCell className="text-right tabular-nums">{money(line.unitPrice)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{money(line.lineTotal)}</TableCell>
                                        <TableCell><LineStatusPill status={line.lineStatus} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 mt-4 text-sm">
                        <span className="text-muted-foreground">
                            Ordered <span className="font-medium text-foreground tabular-nums">{order.orderedUnits}</span>
                        </span>
                        <span className="text-muted-foreground">
                            Received <span className="font-medium text-foreground tabular-nums">{order.receivedUnits}</span>
                        </span>
                        <span className="text-muted-foreground">
                            Total <span className="font-semibold text-foreground tabular-nums">{money(order.totalValue)}</span>
                        </span>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-4 md:p-6 space-y-3">
                    <div>
                        <h2 className="font-semibold">Receipts</h2>
                        <p className="text-xs text-muted-foreground mt-1">
                            Every delivery is its own receipt. Only posted receipts count toward the
                            quantities above — voiding one rolls them back.
                        </p>
                    </div>

                    {order.receipts.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-2">
                            Nothing received yet. Partial receiving is normal: a receipt is logged
                            each time a delivery lands, and the PO only closes once every line is
                            fully received — or it is closed short.
                        </p>
                    ) : order.receipts.map((receipt) => {
                        const receiptStatus = receipt.status;
                        const editable = receiptStatus === 'in_progress';
                        return (
                            <div key={receipt.id} className="rounded-lg border border-border p-3 space-y-3">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                        <p className="font-mono text-sm font-medium">{receipt.docketNumber}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatDate(receipt.receiptDate)} · received by {receipt.receivedBy.name}
                                            {' · '}
                                            {receipt.carrier || 'Carrier not recorded'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap ${RECEIPT_STATUS_STYLES[receiptStatus]}`}>
                                            {RECEIPT_STATUS_LABELS[receiptStatus]}
                                        </span>
                                        {editable && abilities.canReceipt && (
                                            <Button variant="outline" size="sm"
                                                    onClick={() => navigate(`/stock/procurement/receiving/${order.id}`)}>
                                                Resume
                                            </Button>
                                        )}
                                        {receiptStatus === 'posted' && abilities.canReceipt && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="text-destructive"
                                                disabled={busy !== null}
                                                onClick={() => {
                                                    setReason('');
                                                    setReasonDialog({
                                                        kind: 'void', receiptId: receipt.id,
                                                        reference: receipt.docketNumber,
                                                    });
                                                }}
                                            >
                                                <Ban size={14} className="mr-1.5" />Void
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {receipt.status === 'voided' && receipt.voidReason && (
                                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                        Voided {formatDateTime(receipt.voidedAt)} — {receipt.voidReason}
                                    </p>
                                )}
                                {receipt.notes && (
                                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{receipt.notes}</p>
                                )}

                                {receipt.lines.length > 0 && (
                                    <div className="divide-y divide-border border border-border rounded-md">
                                        {receipt.lines.map((line) => (
                                            <div key={line.id} className="flex items-center gap-2 p-2 text-sm">
                                                <span className="font-medium tabular-nums">
                                                    {line.quantityReceived} ×
                                                </span>
                                                <span className="flex-1 min-w-0 truncate">{line.description}</span>
                                                <span className="text-xs text-muted-foreground">{line.uom}</span>
                                                <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md whitespace-nowrap ${CONDITION_STYLES[line.condition]}`}>
                                                    {CONDITION_LABELS[line.condition]}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {(receipt.photos.length > 0 || (editable && abilities.canUploadPhoto)) && (
                                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                        {receipt.photos.map((photo: PurchaseOrderPhoto) => (
                                            <div key={photo.id} className="relative aspect-square">
                                                <button
                                                    type="button"
                                                    className="h-full w-full overflow-hidden rounded-md border border-border"
                                                    onClick={() => setLightboxIndex(photoPositions.get(photo.id) ?? null)}
                                                    aria-label={`Open photo ${photo.id}`}
                                                >
                                                    <img src={photo.url} alt="" className="h-full w-full object-cover" />
                                                </button>
                                                {editable && abilities.canDeletePhoto && (
                                                    <button
                                                        type="button"
                                                        aria-label="Remove photo"
                                                        onClick={() => setRemoving({
                                                            kind: 'photo', receiptId: receipt.id, id: photo.id,
                                                            label: `Photo of ${receipt.docketNumber}`,
                                                        })}
                                                        className="absolute top-1 right-1 h-6 w-6 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-destructive focus:outline-none focus:ring-2 focus:ring-ring"
                                                    >
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {editable && abilities.canUploadPhoto && (
                                            <label className={`aspect-square w-full rounded-md border border-dashed border-input cursor-pointer flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-accent ${busy !== null ? 'opacity-50 pointer-events-none' : ''}`}>
                                                {busy === `photo:${receipt.id}`
                                                    ? <Loader2 size={16} className="animate-spin" />
                                                    : <Camera size={16} />}
                                                Add photo
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    capture="environment"
                                                    className="hidden"
                                                    ref={(el) => { photoInputs.current[receipt.id] = el; }}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        e.target.value = '';
                                                        if (file) uploadPhoto(receipt.id, file);
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                )}

                                <div className="flex flex-wrap items-center gap-2">
                                    {receipt.documents.map((document: PurchaseOrderDocument) => (
                                        <span key={document.id}
                                              className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 px-3 py-2 text-xs">
                                            <FileText size={14} />
                                            <a href={document.url} target="_blank" rel="noreferrer"
                                               className="underline truncate max-w-[12rem]">
                                                {document.filename}
                                            </a>
                                            {editable && abilities.canDeleteDocument && (
                                                <button
                                                    type="button"
                                                    aria-label="Remove document"
                                                    onClick={() => setRemoving({
                                                        kind: 'document', receiptId: receipt.id,
                                                        id: document.id, label: document.filename,
                                                    })}
                                                    className="text-blue-700 hover:text-destructive"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </span>
                                    ))}
                                    {editable && abilities.canUploadDocument && (
                                        <>
                                            <input
                                                type="file"
                                                accept="application/pdf,image/*"
                                                className="hidden"
                                                ref={(el) => { documentInputs.current[receipt.id] = el; }}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    e.target.value = '';
                                                    if (file) uploadDocument(receipt.id, file);
                                                }}
                                            />
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={busy !== null}
                                                onClick={() => documentInputs.current[receipt.id]?.click()}
                                            >
                                                {busy === `document:${receipt.id}`
                                                    ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                                    : <Paperclip size={14} className="mr-1.5" />}
                                                {receipt.documents.length
                                                    ? 'Attach another'
                                                    : 'Attach docket / invoice'}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </CardContent>
            </Card>

            <AmendmentPanel order={order} onChanged={load} />

            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-3">History</h2>
                    {history.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>
                    ) : (
                        <>
                            <ol className="space-y-0">
                                {historyWindow.map((entry) => (
                                    <li key={entry.id}
                                        className="relative border-l border-border pl-5 pb-4 last:pb-0">
                                        <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-sm">
                                                {entry.fromStatus
                                                    ? `${purchaseOrderStatusLabel(entry.fromStatus)} → ${purchaseOrderStatusLabel(entry.toStatus)}`
                                                    : purchaseOrderStatusLabel(entry.toStatus)}
                                            </span>
                                            {entry.ownerType === 'purchase_order_receipt' && (
                                                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                                                    Receipt
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {entry.actorName} · {formatDateTime(entry.createdAt)}
                                        </p>
                                        {entry.reason && (
                                            <p className="text-xs text-amber-800 mt-1 leading-relaxed">{entry.reason}</p>
                                        )}
                                    </li>
                                ))}
                            </ol>

                            {/* A long-lived PO collects hundreds of events; ten at a time keeps
                                the card scannable and the newest page is the one shown first. */}
                            {historyPages > 1 && (
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground">
                                        Showing <span className="font-medium">{historyFrom + 1}</span>–
                                        <span className="font-medium">{historyTo}</span> of{' '}
                                        <span className="font-medium">{history.length}</span> events
                                    </p>
                                    <div className="flex items-center gap-1">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            aria-label="Previous page"
                                            disabled={safeHistoryPage === 1}
                                            onClick={() => setHistoryPage(safeHistoryPage - 1)}
                                        >
                                            <ChevronLeft size={16} />
                                        </Button>
                                        <Pagination className="mx-0 w-auto">
                                            <PaginationContent className="gap-1">
                                                {pageWindow(historyPages, safeHistoryPage).map((page) => (
                                                    typeof page === 'number' ? (
                                                        <PaginationItem key={page}>
                                                            <PaginationLink
                                                                href="#"
                                                                isActive={page === safeHistoryPage}
                                                                onClick={(event) => {
                                                                    event.preventDefault();
                                                                    setHistoryPage(page);
                                                                }}
                                                                className="h-8 w-8 p-0 text-xs"
                                                            >
                                                                {page}
                                                            </PaginationLink>
                                                        </PaginationItem>
                                                    ) : (
                                                        <PaginationItem key={page}>
                                                            <PaginationEllipsis />
                                                        </PaginationItem>
                                                    )
                                                ))}
                                            </PaginationContent>
                                        </Pagination>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            aria-label="Next page"
                                            disabled={safeHistoryPage === historyPages}
                                            onClick={() => setHistoryPage(safeHistoryPage + 1)}
                                        >
                                            <ChevronRight size={16} />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ---------------------------------------------------------- dialogs */}

            <Dialog open={reasonDialog !== null}
                    onOpenChange={(open) => { if (!open) { setReasonDialog(null); setReason(''); } }}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>
                            {reasonCopy?.title(reasonDialog?.reference ?? reference) ?? 'Reason'}
                        </DialogTitle>
                        <DialogDescription>{reasonCopy?.body}</DialogDescription>
                    </DialogHeader>

                    <div className="space-y-2">
                        <Label htmlFor="po-reason">
                            {reasonCopy && reasonCopy.min > 1
                                ? `Reason — minimum ${reasonCopy.min} characters`
                                : 'Reason — shown to the requester'}
                        </Label>
                        <Textarea
                            id="po-reason"
                            rows={4}
                            value={reason}
                            autoFocus
                            placeholder={reasonCopy?.placeholder}
                            onChange={(e) => setReason(e.target.value)}
                        />
                        {reasonCopy && reasonCopy.min > 1 && (
                            <p className={`text-xs ${reasonValid ? 'text-emerald-700' : 'text-amber-800'}`}>
                                {reason.trim().length} / {reasonCopy.min} characters
                            </p>
                        )}
                        {/* Close Short writes the outstanding quantity off, so the money is worth
                            showing before the click. Cancel calls the PO off without writing
                            anything off, so a breakdown here would claim a loss that is not
                            recorded. */}
                        {reasonDialog?.kind === 'close' && impact.length > 0 && (
                            <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
                                <p className="text-xs font-medium text-amber-900">
                                    This writes off:
                                </p>
                                <ul className="mt-1 space-y-0.5">
                                    {impact.map((line) => (
                                        <li key={line.id} className="text-xs text-amber-900 flex justify-between gap-3">
                                            <span>{line.outstanding} × {line.description} ({line.uom})</span>
                                            <span className="tabular-nums">−{money(line.outstanding * line.unitPrice)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" disabled={busy !== null}
                                onClick={() => { setReasonDialog(null); setReason(''); }}>
                            Cancel
                        </Button>
                        <Button
                            variant={reasonDialog?.kind === 'reject' ? 'destructive' : 'default'}
                            disabled={!reasonValid || busy !== null}
                            onClick={confirmReason}
                        >
                            {busy !== null && <Loader2 size={16} className="animate-spin mr-2" />}
                            {reasonCopy?.confirm}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={enterOpen} onOpenChange={setEnterOpen}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Enter {reference}</DialogTitle>
                        <DialogDescription>
                            Records that this PO has been keyed into both downstream systems.
                            Terminal — the record locks afterwards.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="po-external-ref">External reference *</Label>
                            <Input
                                id="po-external-ref"
                                value={externalRef}
                                placeholder="The reference the accounting systems allocated"
                                onChange={(e) => setExternalRef(e.target.value)}
                            />
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                                checked={enteredInventory}
                                onCheckedChange={(checked) => setEnteredInventory(checked === true)}
                            />
                            <span className="text-sm">Entered into inventory software</span>
                        </label>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <Checkbox
                                checked={enteredApprovalMax}
                                onCheckedChange={(checked) => setEnteredApprovalMax(checked === true)}
                            />
                            <span className="text-sm">Entered into ApprovalMax</span>
                        </label>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEnterOpen(false)} disabled={busy !== null}>
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmEnter}
                            disabled={busy !== null || !externalRef.trim()
                                || !enteredInventory || !enteredApprovalMax}
                        >
                            {busy === 'enter' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Mark as Entered
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent className="sm:max-w-[460px]">
                    <DialogHeader>
                        <DialogTitle>Delete {reference}?</DialogTitle>
                        <DialogDescription>
                            A draft is the only thing that can be deleted. Anything that has been
                            submitted is cancelled instead, so the history survives.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={busy !== null}>
                            Keep draft
                        </Button>
                        <Button variant="destructive" onClick={confirmDelete} disabled={busy !== null}>
                            {busy === 'delete' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Delete draft
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={editOpen} onOpenChange={(open) => { if (!open) setEditOpen(false); }}>
                <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Edit {reference}</DialogTitle>
                        <DialogDescription>
                            {'Header and lines are saved together. A quantity cannot drop below what ' +
                                'has already been received, and every changed line is written to the history.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Vendor</Label>
                                <Combobox
                                    options={vendors.map((vendor) => ({
                                        label: vendor.tradingName || vendor.legalName,
                                        value: String(vendor.id),
                                        subtitle: vendor.legalName === vendor.tradingName ? undefined : vendor.legalName,
                                    }))}
                                    value={editVendorId || undefined}
                                    onChange={(value) => setEditVendorId(value || '')}
                                    placeholder={vendors.length ? 'Select a vendor' : 'No active vendors'}
                                    searchPlaceholder="Search vendors..."
                                    emptyMessage="No vendors found."
                                />
                            </div>
                            <div>
                                <Label>Project</Label>
                                <Combobox
                                    options={projects.map((project) => ({
                                        label: `${project.projectName} (${project.projectNumber})`,
                                        value: String(project.id),
                                    }))}
                                    value={editProjectId || undefined}
                                    onChange={(value) => setEditProjectId(value || '')}
                                    placeholder="No project (yard / overhead)"
                                    searchPlaceholder="Search projects..."
                                    emptyMessage="No projects in this branch."
                                />
                            </div>
                            <div>
                                <Label htmlFor="po-edit-required-by">Required by</Label>
                                <Input
                                    id="po-edit-required-by"
                                    type="date"
                                    value={editRequiredBy}
                                    onChange={(e) => setEditRequiredBy(e.target.value)}
                                />
                            </div>
                            <div>
                                <Label>Branch</Label>
                                <p className="text-sm mt-2">{order.branch.name}</p>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="po-edit-notes">Notes</Label>
                            <Textarea
                                id="po-edit-notes"
                                rows={2}
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                            />
                        </div>

                        <div className="space-y-3">
                            <div>
                                <h3 className="text-sm font-semibold">Lines</h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                    Removing a line removes it from the order; a line with receipts
                                    against it cannot be removed or dropped below what was received.
                                </p>
                            </div>
                            <PurchaseOrderLineEditor
                                lines={editLines}
                                onChange={setEditLines}
                                products={products}
                                disabled={busy !== null}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy !== null}>
                            Cancel
                        </Button>
                        <Button onClick={saveEdit}
                                disabled={busy !== null
                                    || lineDraftPayloads(editLines).length === 0
                                    || orderDraftIssue(editLines) !== null}>
                            {busy === 'edit' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Save changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={renamingDocument !== null}
                    onOpenChange={(open) => {
                        if (!open) { setRenamingDocument(null); setRenameFilename(''); }
                    }}>
                <DialogContent className="sm:max-w-[460px]">
                    <DialogHeader>
                        <DialogTitle>Rename attachment</DialogTitle>
                        <DialogDescription>
                            The file extension is kept from {renamingDocument?.filename}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="po-document-rename">Filename</Label>
                        <Input
                            id="po-document-rename"
                            value={renameFilename}
                            onChange={(e) => setRenameFilename(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy !== null}
                                onClick={() => { setRenamingDocument(null); setRenameFilename(''); }}>
                            Cancel
                        </Button>
                        <Button onClick={confirmRenameDocument}
                                disabled={busy !== null || !renameFilename.trim()}>
                            {busy === 'rename-document' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Rename
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={removing !== null}
                    onOpenChange={(open) => { if (!open) setRemoving(null); }}>
                <DialogContent className="sm:max-w-[460px]">
                    <DialogHeader>
                        <DialogTitle>
                            Remove this {removing?.kind === 'photo' ? 'photo' : 'document'}?
                        </DialogTitle>
                        <DialogDescription>
                            {removing?.kind === 'order-document'
                                ? `${removing?.label} will be removed from this purchase order. ` +
                                    'The removal is kept in the history.'
                                : `${removing?.label} — evidence attached to an in-progress receipt ` +
                                    'can be removed; a posted receipt keeps everything it was signed off with.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemoving(null)} disabled={busy !== null}>
                            Keep it
                        </Button>
                        <Button variant="destructive" onClick={confirmRemove} disabled={busy !== null}>
                            {busy === 'remove' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Remove
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImageLightbox
                alt="Receipt photo"
                images={lightboxImages}
                index={lightboxIndex}
                onIndexChange={setLightboxIndex}
            />
        </div>
    );
}
